import { useSession } from 'components/auth/useSession'
import { usePecField } from 'components/form/core/hooks/useField'
import { SemanasDiasFieldModel } from 'components/form/field/SemanasDiasField'
import { PecSwitch } from 'components/route'
import { useMemo } from 'react'
import { match, Redirect, Route } from 'react-router'
import { emptyArrayOf } from 'util/array'
import { meta } from 'view/atendimentos/atendimento-individual/model'
import { grupoCboMedicoes } from 'view/atendimentos/detail/soap/objetivo/acessos'
import { TipoPreNatal } from 'view/atendimentos/detail/soap/pre-natal/model-preNatal'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import { convertMedicaoAnterior, convertMedicaoAnteriorRetificacao } from './converter-medicoes'
import { HistoricoMedicoesExameFisicoView } from './exame-fisico/HistoricoMedicoesExameFisicoView'
import { MODAL_MEDICAO_ANTERIOR_EXAME_FISICO_TAB_URL } from './exame-fisico/model-medicaoAnteriorExameFisico'
import { isMedicaoAnteriorExameFisico } from './exame-fisico/utils-medicaoAnteriorExameFisico'
import { HistoricoMedicoesAntropometriaView } from './HistoricoMedicoesAntropometriaView'
import { HistoricoMedicoesGlicemiaView } from './HistoricoMedicoesGlicemiaView'
import { EditableHistoricoMedicoesModalProps } from './HistoricoMedicoesModal'
import { HistoricoMedicoesSinaisVitaisView } from './HistoricoMedicoesSinaisVitaisView'
import { MedicaoAnteriorModel } from './model-medicoes'
import { HistoricoMedicoesPreNatalView } from './pre-natal/HistoricoMedicoesPreNatalView'
import { getMedicoesAntropometricasPreNatal } from './pre-natal/util-medicoesPreNatal'

interface EditableHistoricoMedicoesModalBodyProps extends EditableHistoricoMedicoesModalProps {
  initialRoute: string
  match: match
  isAtendimentoObservacao: boolean
  tipoPreNatal?: TipoPreNatal
  isRetificacao: boolean
  isGestante: boolean
}

export function EditableHistoricoMedicoesModalBody({
  fieldName,
  prontuarioId,
  cidadao,
  dataAtendimento,
  initialRoute,
  match,
  medicoes = [],
  preNatalAtendimentoAtual,
  dumAtendimentoAtual,
  resultadosExamesAtendimentoAtual,
  gestacoes,
  isAtendimentoObservacao,
  tipoPreNatal,
  isRetificacao,
  isAtendimentoOdonto,
  isAtendimentoProcedimentos,
  isGestante,
}: Readonly<EditableHistoricoMedicoesModalBodyProps>) {
  const formField = usePecField({ name: fieldName })
  const { checkAuthorization } = useSession()

  const canEditOrAddExameFisico =
    checkAuthorization(grupoCboMedicoes.adicionar) && !isAtendimentoOdonto && !isAtendimentoProcedimentos

  const medicoesAnteriores: ReadonlyArray<MedicaoAnteriorModel> = useMemo(
    () => formField.input.value ?? emptyArrayOf<MedicaoAnteriorModel>(),
    [formField.input.value]
  )

  const medicoesExibir: HistoricoMedicaoModel[] = useMemo(() => {
    const anterioresConvertidas = isRetificacao
      ? medicoesAnteriores.map(convertMedicaoAnteriorRetificacao)
      : medicoesAnteriores.map(convertMedicaoAnterior)

    return isRetificacao ? anterioresConvertidas : [...anterioresConvertidas, ...medicoes]
  }, [medicoes, medicoesAnteriores, isRetificacao])

  const medicoesAnterioesExameFisico = medicoesExibir.filter((medicaoExibir) =>
    isMedicaoAnteriorExameFisico(medicaoExibir)
  )

  const onAddMedicaoAnterior = (medicao: MedicaoAnteriorModel) => {
    formField.input.onChange([medicao, ...formField.input.value])
    formField.input.onBlur()
  }

  const onRemoveMedicaoAnterior = (id: ID) => {
    formField.input.onChange([...(formField.input.value ?? []).filter((m: MedicaoAnteriorModel) => m.id !== id)])
    formField.input.onBlur()
  }

  const {
    input: { value: idadeGestacionalAtendimentoAtual },
  } = usePecField<SemanasDiasFieldModel | undefined>({
    name: meta?.objetivo?.atendimentosEspecificos?.puericultura?.prenatalPartoNascimento?.idadeGestacional,
    subscription: { value: true },
  })

  return (
    <PecSwitch>
      <Redirect exact path={match.url} to={`${match.url}${initialRoute}`} />
      {!isRetificacao && (
        <Route
          path={`${match.url}/pre-natal`}
          render={(renderProps) => (
            <HistoricoMedicoesPreNatalView
              {...renderProps}
              prontuarioId={prontuarioId}
              preNatalAtendimentoAtual={preNatalAtendimentoAtual}
              resultadosExamesAtendimentoAtual={resultadosExamesAtendimentoAtual}
              dumAtendimentoAtual={dumAtendimentoAtual}
              dataAtendimento={dataAtendimento}
              medicoesAntropometricasPreNatal={getMedicoesAntropometricasPreNatal(medicoesExibir, tipoPreNatal)}
              tipoPreNatal={tipoPreNatal}
            />
          )}
        />
      )}
      <Route
        path={`${match.url}/antropometria`}
        render={(renderProps) => (
          <HistoricoMedicoesAntropometriaView
            {...renderProps}
            prontuarioId={prontuarioId}
            canEditOrAdd={checkAuthorization(grupoCboMedicoes.adicionar)}
            medicoes={medicoesExibir}
            cidadao={cidadao}
            gestacoes={gestacoes}
            isGestante={isGestante}
            dataAtendimento={dataAtendimento}
            isAtendimentoObservacao={isAtendimentoObservacao}
            isRetificacao={isRetificacao}
            onAddMedicaoAnterior={onAddMedicaoAnterior}
            onRemoveMedicaoAnterior={onRemoveMedicaoAnterior}
            idadeGestacionalAtendimentoAtual={idadeGestacionalAtendimentoAtual}
          />
        )}
      />
      {!isRetificacao && (
        <>
          <Route
            path={`${match.url}/sinaisVitais`}
            render={(renderProps) => (
              <HistoricoMedicoesSinaisVitaisView {...renderProps} medicoes={medicoesExibir} gestacoes={gestacoes} />
            )}
          />
          <Route
            path={`${match.url}/glicemia`}
            render={(renderProps) => (
              <HistoricoMedicoesGlicemiaView {...renderProps} medicoes={medicoesExibir} gestacoes={gestacoes} />
            )}
          />
          <Route
            path={`${match.url}${MODAL_MEDICAO_ANTERIOR_EXAME_FISICO_TAB_URL}`}
            render={(renderProps) => (
              <HistoricoMedicoesExameFisicoView
                {...renderProps}
                dataAtendimento={dataAtendimento}
                dataNascimentoCidadao={cidadao.dataNascimento}
                isAtendimentoObservacao={isAtendimentoObservacao}
                medicoes={medicoesAnterioesExameFisico}
                canEditOrAdd={canEditOrAddExameFisico}
                onAddMedicaoAnterior={onAddMedicaoAnterior}
                onRemoveMedicaoAnterior={onRemoveMedicaoAnterior}
              />
            )}
          />
        </>
      )}
    </PecSwitch>
  )
}
